/**
 * PWA Detection Utilities
 * Consolida la lógica de instalación siguiendo las recomendaciones de Google
 * para PWAs profesionales en múltiples dispositivos.
 */

const STORAGE_KEYS = {
  INSTALLED: "pwa-installed",
  INSTALL_DATE: "pwa-install-date",
  LAST_PROMPT_AT: "pwa-install-last-prompt-at",
  LAST_DISMISSED_AT: "pwa-install-last-dismissed-at",
  LEGACY_DISMISSED: "pwa-install-dismissed",
  LEGACY_LAST_SHOWN: "pwa-install-last-shown",
};

const PROMPT_COOLDOWN_HOURS = 6;
const DISMISS_COOLDOWN_HOURS = 24;

export class PWADetector {
  static now() {
    return Date.now();
  }

  static toNumber(value) {
    if (!value) {
      return undefined;
    }

    const numeric = Number(value);
    if (!Number.isNaN(numeric)) {
      return numeric;
    }

    const parsed = Date.parse(value);
    if (!Number.isNaN(parsed)) {
      return parsed;
    }

    return undefined;
  }

  static hoursSince(timestamp) {
    if (timestamp === undefined) {
      return Infinity;
    }

    return (this.now() - timestamp) / (1000 * 60 * 60);
  }

  static readTimestamp(primaryKey, legacyKey, treatBooleanAsNow = false) {
    const primary = this.toNumber(localStorage.getItem(primaryKey));
    if (primary !== undefined) {
      return primary;
    }

    if (!legacyKey) {
      return undefined;
    }

    const legacyValue = localStorage.getItem(legacyKey);
    if (!legacyValue) {
      return undefined;
    }

    if (legacyValue === "true") {
      if (!treatBooleanAsNow) {
        return undefined;
      }

      const timestamp = this.now();
      localStorage.setItem(legacyKey, String(timestamp));
      return timestamp;
    }

    return this.toNumber(legacyValue);
  }

  /**
   * Detecta si la aplicación está corriendo en modo PWA/Standalone.
   */
  static isPWAMode() {
    if (window.navigator.standalone === true) {
      return true;
    }

    if (window.matchMedia("(display-mode: standalone)").matches) {
      return true;
    }

    if (window.matchMedia("(display-mode: minimal-ui)").matches) {
      return true;
    }

    return false;
  }

  static getOS() {
    const userAgent = window.navigator.userAgent.toLowerCase();
    const platform = window.navigator.platform.toLowerCase();

    if (/iphone|ipad|ipod/.test(userAgent)) {
      return "ios";
    }

    if (/android/.test(userAgent)) {
      return "android";
    }

    if (/win/.test(platform)) {
      return "windows";
    }

    if (/mac/.test(platform)) {
      return "macos";
    }

    if (/linux/.test(platform)) {
      return "linux";
    }

    return "unknown";
  }

  static getBrowser() {
    const userAgent = window.navigator.userAgent.toLowerCase();

    if (/edg\//.test(userAgent)) {
      return "edge";
    }

    if (
      /chrome\//.test(userAgent) &&
      !/edg\//.test(userAgent) &&
      !/opr\//.test(userAgent)
    ) {
      return "chrome";
    }

    if (/safari/.test(userAgent) && !/chrome/.test(userAgent)) {
      return "safari";
    }

    if (/firefox/.test(userAgent)) {
      return "firefox";
    }

    if (/samsungbrowser/.test(userAgent)) {
      return "samsung";
    }

    if (/opr\//.test(userAgent) || /opera/.test(userAgent)) {
      return "opera";
    }

    return "unknown";
  }

  static isMobile() {
    return /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
      window.navigator.userAgent
    );
  }

  static isTablet() {
    const ua = window.navigator.userAgent;
    return /(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua);
  }

  static supportsPWA() {
    return "serviceWorker" in navigator && window.isSecureContext;
  }

  static canShowInstallPrompt() {
    if (this.isPWAMode()) {
      return false;
    }

    if (this.isInstalled()) {
      return false;
    }

    const os = this.getOS();
    const browser = this.getBrowser();

    if (os === "ios" && browser === "safari") {
      return false;
    }

    return ["chrome", "edge", "samsung"].includes(browser);
  }

  static isInstalled() {
    if (this.isPWAMode()) {
      return true;
    }

    return localStorage.getItem(STORAGE_KEYS.INSTALLED) === "true";
  }

  static markAsInstalled() {
    localStorage.setItem(STORAGE_KEYS.INSTALLED, "true");
    localStorage.setItem(STORAGE_KEYS.INSTALL_DATE, new Date().toISOString());
  }

  static shouldShowInstallBanner() {
    if (this.isInstalled()) {
      return false;
    }

    const dismissedAt = this.readTimestamp(
      STORAGE_KEYS.LAST_DISMISSED_AT,
      STORAGE_KEYS.LEGACY_DISMISSED,
      true
    );
    if (this.hoursSince(dismissedAt) < DISMISS_COOLDOWN_HOURS) {
      return false;
    }

    const lastPromptAt = this.readTimestamp(
      STORAGE_KEYS.LAST_PROMPT_AT,
      STORAGE_KEYS.LEGACY_LAST_SHOWN
    );
    if (this.hoursSince(lastPromptAt) < PROMPT_COOLDOWN_HOURS) {
      return false;
    }

    return true;
  }

  static markBannerAsShown() {
    const timestamp = this.now();
    localStorage.setItem(STORAGE_KEYS.LAST_PROMPT_AT, String(timestamp));
    localStorage.setItem(
      STORAGE_KEYS.LEGACY_LAST_SHOWN,
      new Date(timestamp).toISOString()
    );
  }

  static markBannerAsDismissed() {
    const timestamp = this.now();
    localStorage.setItem(STORAGE_KEYS.LAST_DISMISSED_AT, String(timestamp));
    localStorage.setItem(STORAGE_KEYS.LEGACY_DISMISSED, String(timestamp));
  }

  static resetInstallState() {
    localStorage.removeItem(STORAGE_KEYS.INSTALLED);
    localStorage.removeItem(STORAGE_KEYS.INSTALL_DATE);
    localStorage.removeItem(STORAGE_KEYS.LAST_PROMPT_AT);
    localStorage.removeItem(STORAGE_KEYS.LAST_DISMISSED_AT);
    localStorage.removeItem(STORAGE_KEYS.LEGACY_DISMISSED);
    localStorage.removeItem(STORAGE_KEYS.LEGACY_LAST_SHOWN);
  }

  static resetBannerCooldowns() {
    localStorage.removeItem(STORAGE_KEYS.LAST_PROMPT_AT);
    localStorage.removeItem(STORAGE_KEYS.LAST_DISMISSED_AT);
    localStorage.removeItem(STORAGE_KEYS.LEGACY_DISMISSED);
    localStorage.removeItem(STORAGE_KEYS.LEGACY_LAST_SHOWN);
  }

  static getDeviceInfo() {
    const dismissedAt = this.readTimestamp(
      STORAGE_KEYS.LAST_DISMISSED_AT,
      STORAGE_KEYS.LEGACY_DISMISSED,
      true
    );
    const lastPromptAt = this.readTimestamp(
      STORAGE_KEYS.LAST_PROMPT_AT,
      STORAGE_KEYS.LEGACY_LAST_SHOWN
    );

    return {
      os: this.getOS(),
      browser: this.getBrowser(),
      isMobile: this.isMobile(),
      isTablet: this.isTablet(),
      isPWAMode: this.isPWAMode(),
      isInstalled: this.isInstalled(),
      supportsPWA: this.supportsPWA(),
      canShowInstallPrompt: this.canShowInstallPrompt(),
      shouldShowBanner: this.shouldShowInstallBanner(),
      lastPromptAt,
      lastDismissedAt: dismissedAt,
      hoursSincePrompt: this.hoursSince(lastPromptAt),
      hoursSinceDismissed: this.hoursSince(dismissedAt),
      userAgent: window.navigator.userAgent,
      platform: window.navigator.platform,
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
    };
  }
}

export default PWADetector;
